import type { BroadcastSenderMode } from '../../types/broadcast';

// Stage 6A §5.3 — From + Reply-To selection (email only). Three modes each,
// chosen independently; default both to Account Manager. Switching to/from
// 'custom' toggles the custom field between '' and null so the saved draft
// always satisfies the sender field-coupling (non-custom ⇒ null).

export type SenderFields = {
  emailFromMode: BroadcastSenderMode;
  emailFromCustomName: string | null;
  emailReplyToMode: BroadcastSenderMode;
  emailReplyToCustomEmail: string | null;
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const MODE_OPTIONS: Array<{ value: BroadcastSenderMode; label: string }> = [
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'initiator', label: 'Me' },
  { value: 'custom', label: 'Custom' },
];

export function SenderSelect({
  value,
  onChange,
}: {
  value: SenderFields;
  onChange: (patch: Partial<SenderFields>) => void;
}) {
  const setFromMode = (mode: BroadcastSenderMode) =>
    onChange({
      emailFromMode: mode,
      emailFromCustomName: mode === 'custom' ? (value.emailFromCustomName ?? '') : null,
    });

  const setReplyMode = (mode: BroadcastSenderMode) =>
    onChange({
      emailReplyToMode: mode,
      emailReplyToCustomEmail: mode === 'custom' ? (value.emailReplyToCustomEmail ?? '') : null,
    });

  const replyEmailInvalid =
    value.emailReplyToMode === 'custom' &&
    (value.emailReplyToCustomEmail ?? '').trim().length > 0 &&
    !EMAIL_RE.test((value.emailReplyToCustomEmail ?? '').trim());

  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3">
      <h3 className="text-sm font-semibold text-slate-700">Sender</h3>

      <fieldset>
        <legend className="mb-1 text-sm font-medium text-slate-700">From</legend>
        <RadioRow name="from" mode={value.emailFromMode} onSelect={setFromMode} />
        {value.emailFromMode === 'custom' && (
          <input
            type="text"
            value={value.emailFromCustomName ?? ''}
            onChange={(e) => onChange({ emailFromCustomName: e.target.value })}
            placeholder="e.g. Fitstop HQ"
            className="mt-2 w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        )}
      </fieldset>

      <fieldset>
        <legend className="mb-1 text-sm font-medium text-slate-700">Reply-To</legend>
        <RadioRow name="replyto" mode={value.emailReplyToMode} onSelect={setReplyMode} />
        {value.emailReplyToMode === 'custom' && (
          <>
            <input
              type="email"
              value={value.emailReplyToCustomEmail ?? ''}
              onChange={(e) => onChange({ emailReplyToCustomEmail: e.target.value })}
              placeholder="e.g. hq@fitstop.com.au"
              className="mt-2 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
            {replyEmailInvalid && (
              <p className="mt-1 text-xs text-amber-700">
                That doesn't look like a valid email — you can keep editing, but sending will be
                blocked until it's valid.
              </p>
            )}
          </>
        )}
      </fieldset>
    </div>
  );
}

function RadioRow({
  name,
  mode,
  onSelect,
}: {
  name: string;
  mode: BroadcastSenderMode;
  onSelect: (mode: BroadcastSenderMode) => void;
}) {
  return (
    <div className="flex flex-wrap gap-4">
      {MODE_OPTIONS.map((o) => (
        <label key={o.value} className="flex items-center gap-1.5 text-sm">
          <input
            type="radio"
            name={name}
            checked={mode === o.value}
            onChange={() => onSelect(o.value)}
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}
